import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} VariantDescriptionRefs
 * @property {HTMLElement} contentContainer
 * @property {HTMLElement} status
 */

/**
 * Keeps a server-rendered variant metaobject description synchronized with the
 * selected product variant.
 *
 * @extends {Component<VariantDescriptionRefs>}
 */
class VariantDescription extends Component {
  requiredRefs = ['contentContainer', 'status'];

  /** @type {Element | null} */
  #eventTarget = null;

  #selectionSequence = 0;

  #statusClearTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#selectionSequence += 1;
    window.clearTimeout(this.#statusClearTimeout);
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
    super.disconnectedCallback();
  }

  /**
   * Updates this block from the authoritative HTML already fetched by Horizon's
   * variant picker. A local sequence guard prevents a superseded response from
   * replacing a newer selection.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const sourceProductId = event.target.closest('variant-picker')?.getAttribute('data-product-id');
    if (sourceProductId && sourceProductId !== this.dataset.productId) return;

    const selectionSequence = ++this.#selectionSequence;
    this.setAttribute('aria-busy', 'true');
    window.clearTimeout(this.#statusClearTimeout);
    this.refs.status.textContent = '';
    const renderedFromCache = this.#renderCachedDescription(event.detail?.variantId);

    event.promise
      .then(({ detail }) => {
        if (selectionSequence !== this.#selectionSequence || !this.isConnected) return;

        if (!detail?.html) {
          this.removeAttribute('aria-busy');
          return;
        }

        if (!detail.newProduct && detail.productId && detail.productId !== this.dataset.productId) {
          this.removeAttribute('aria-busy');
          return;
        }

        const blockId = this.dataset.blockId;
        const newVariantDescription = Array.from(detail.html.querySelectorAll('variant-description')).find(
          (element) => element instanceof HTMLElement && element.dataset.blockId === blockId
        );

        if (!(newVariantDescription instanceof HTMLElement)) {
          this.removeAttribute('aria-busy');
          console.warn(`[variant-description] No updated block source found for ${blockId ?? 'unknown block'}.`);
          return;
        }

        const newContent = newVariantDescription.querySelector('[ref="contentContainer"]');
        if (!(newContent instanceof HTMLElement)) {
          this.removeAttribute('aria-busy');
          console.warn(`[variant-description] No updated content found for ${blockId ?? 'unknown block'}.`);
          return;
        }

        morph(this.refs.contentContainer, newContent, {
          ...MORPH_OPTIONS,
          childrenOnly: false,
        });

        this.#syncDataAttributes(newVariantDescription);
        this.removeAttribute('aria-busy');

        if (!renderedFromCache) this.#announce(newVariantDescription.dataset.announcement?.trim());
      })
      .catch((error) => {
        if (selectionSequence !== this.#selectionSequence || !this.isConnected) return;

        this.removeAttribute('aria-busy');
        if (error?.name !== 'AbortError') console.warn('[variant-description] Product selection update failed:', error);
      });
  };

  /**
   * Renders server-generated rich text already present in the initial page HTML.
   * The selection response still reconciles this content with current Shopify data.
   *
   * @param {unknown} variantId
   * @returns {boolean}
   */
  #renderCachedDescription(variantId) {
    if (variantId === undefined || variantId === null || variantId === '') return false;

    const variantIdString = String(variantId);
    const template = Array.from(this.querySelectorAll('template[data-variant-description-template]')).find(
      (element) => element instanceof HTMLTemplateElement && element.dataset.variantId === variantIdString
    );

    if (!(template instanceof HTMLTemplateElement)) return false;

    const hasDescription = template.dataset.hasDescription === 'true';
    this.refs.contentContainer.replaceChildren(template.content.cloneNode(true));
    this.refs.contentContainer.toggleAttribute('hidden', !hasDescription);
    this.#syncDataAttributes(template);
    this.removeAttribute('aria-busy');
    this.#announce(template.dataset.announcement?.trim());

    return true;
  }

  /**
   * Copies authoritative resource state without replacing the live status node.
   *
   * @param {HTMLElement} source
   */
  #syncDataAttributes(source) {
    for (const attribute of ['productId', 'variantId', 'variantName', 'hasDescription']) {
      const value = source.dataset[attribute];
      if (value) {
        this.dataset[attribute] = value;
      } else {
        delete this.dataset[attribute];
      }
    }
  }

  /**
   * Announces a concise update, then clears the live region so it does not
   * retain stale status text.
   *
   * @param {string | undefined} message
   */
  #announce(message) {
    this.refs.status.textContent = message ?? '';
    if (!message) return;

    this.#statusClearTimeout = window.setTimeout(() => {
      if (this.isConnected) this.refs.status.textContent = '';
    }, 5000);
  }
}

if (!customElements.get('variant-description')) {
  customElements.define('variant-description', VariantDescription);
}
